import {
  $createParagraphNode,
  $getSelection,
  $isLineBreakNode,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  type TextNode,
} from "lexical";
import { $createCodeNode, $isCodeNode } from "@lexical/code";
import { TRANSFORMERS, type Transformer } from "@lexical/markdown";

// Longer code delimiters must be tried before the single-backtick shortcut.
// Export still uses the standard Markdown serializer's safe code delimiters.
export const COMPOSER_MARKDOWN_TRANSFORMERS: Transformer[] = [
  { type: "text-format", tag: "``", format: ["code"] },
  ...TRANSFORMERS,
];

// Use a whole code-span match for typing. Treating each backtick run as a
// generic format tag lets a single backtick inside an unfinished double span
// close the wrong delimiter and destroy literal code characters.
export const COMPOSER_MARKDOWN_SHORTCUTS: Transformer[] = [
  {
    type: "text-match",
    dependencies: [],
    trigger: "`",
    importRegExp: /(?!)/,
    regExp: /(?<![\w`])(``)(?!`)(.+?)\1$|(?<![\w`])(`)(?!`)([^`]+)\3$/,
    replace: (node: TextNode, match: RegExpMatchArray) => {
      const previous = node.getPreviousSibling();
      // A nested single span must not consume code in an open double span.
      if (
        $isTextNode(previous) &&
        (previous.getTextContent().match(/(?<!`)``(?!`)/g)?.length ?? 0) % 2
      )
        return;
      node.setTextContent(match[2] ?? match[4]);
      node.setFormat("code");
      const selection = node.selectEnd();
      if (selection.hasFormat("code")) selection.toggleFormat("code");
    },
  },
  ...COMPOSER_MARKDOWN_TRANSFORMERS.filter(
    (transformer) =>
      transformer.type !== "text-format" ||
      !transformer.format.includes("code"),
  ),
];

/** Format a span as soon as typing lands between existing backtick delimiters. */
export function formatPairedComposerCode(node: TextNode) {
  const selection = $getSelection();
  if (
    !$isRangeSelection(selection) ||
    !selection.isCollapsed() ||
    selection.anchor.key !== node.getKey() ||
    node.hasFormat("code") ||
    node.isToken() ||
    $isCodeNode(node.getTopLevelElement())
  )
    return;
  const offset = selection.anchor.offset;
  const value = node.getTextContent();
  const opening = /(?<!`)(`{1,2})(?!`)([^`]+)$/.exec(value.slice(0, offset));
  if (
    !opening ||
    !value.slice(offset).startsWith(opening[1]) ||
    value[offset + opening[1].length] === "`"
  )
    return;
  const start = opening.index;
  const end = offset + opening[1].length;
  const code =
    start === 0 ? node.splitText(end)[0] : node.splitText(start, end)[1];
  code.setTextContent(opening[2]);
  code.setFormat("code");
  code.selectEnd().setFormat(code.getFormat());
}

/** Return undefined when the normal composer key handling should run. */
export function handleComposerCodeKey(
  event: KeyboardEvent,
): boolean | undefined {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return;
  const anchor = selection.anchor.getNode();
  const block = anchor.getTopLevelElement();
  // Leave an actively edited inline code span with an arrow at its boundary.
  if (
    $isTextNode(anchor) &&
    !event.shiftKey &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.altKey &&
    selection.isCollapsed() &&
    selection.hasFormat("code") &&
    !$isCodeNode(block) &&
    ((event.key === "ArrowRight" &&
      selection.anchor.offset === anchor.getTextContentSize()) ||
      (event.key === "ArrowLeft" && selection.anchor.offset === 0))
  ) {
    selection.toggleFormat("code");
    event.preventDefault();
    return true;
  }

  if ($isCodeNode(block)) {
    // Code stays literal: don't run text-format shortcuts inside it.
    if (
      (event.metaKey || event.ctrlKey) &&
      ["b", "i"].includes(event.key.toLowerCase())
    ) {
      event.preventDefault();
      return true;
    }
    if (event.key !== "Enter" || event.metaKey || event.ctrlKey) return;
    const last = block.getLastChild();
    if (
      selection.isCollapsed() &&
      $isTextNode(anchor) &&
      anchor === last &&
      selection.anchor.offset === anchor.getTextContentSize() &&
      anchor.getTextContent() === "```"
    ) {
      const previous = anchor.getPreviousSibling();
      anchor.remove();
      if ($isLineBreakNode(previous)) previous.remove();
      const paragraph = $createParagraphNode();
      block.insertAfter(paragraph);
      paragraph.select();
      event.preventDefault();
      return true;
    }
    // Lexical inserts code lines (and exits after blank lines) without sending.
    return false;
  }
  if (
    event.key !== "Enter" ||
    event.metaKey ||
    event.ctrlKey ||
    !selection.isCollapsed() ||
    !$isParagraphNode(block)
  )
    return;
  const fence = /^```([a-z0-9_+-]*)$/i.exec(
    block.getTextContent().split("\n").at(-1) ?? "",
  );
  if (
    !fence ||
    !$isTextNode(anchor) ||
    selection.anchor.offset !== anchor.getTextContentSize() ||
    anchor !== block.getLastChild()
  )
    return;
  const code = $createCodeNode(fence[1] || undefined);
  if (block.getTextContent() === fence[0]) {
    block.replace(code);
  } else {
    const previous = anchor.getPreviousSibling();
    if (!$isLineBreakNode(previous) || anchor.getTextContent() !== fence[0])
      return;
    anchor.remove();
    previous.remove();
    block.insertAfter(code);
  }
  code.selectStart();
  event.preventDefault();
  return true;
}

export function isComposerSelectionInCode() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return false;
  const anchor = selection.anchor.getNode();
  return (
    $isCodeNode(anchor.getTopLevelElement()) ||
    ($isTextNode(anchor) && anchor.hasFormat("code"))
  );
}
